import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GridOnIcon from '@mui/icons-material/GridOn';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CategoryIcon from '@mui/icons-material/Category';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GroupsIcon from '@mui/icons-material/Groups';
import CodeIcon from '@mui/icons-material/Code';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import {
  buildWorkspaceDescriptorChips,
  buildWorkspaceDescriptorSidebarItems,
  normalizeWorkspaceDescriptor,
  normalizeWorkspaceDescriptorRoute,
} from '../../../components/common/AnalyticsWorkspace';
import { getDevFlag } from '../../../constants/devFlags';

export const MARKET_COVERAGE_SECTIONS = {
  COVERAGE: 'coverage',
  ACQUISITIONS: 'acquisitions',
  PRIVATE_FUNDING: 'private-funding',
};

export const MARKET_COVERAGE_VIEWS = {
  COVERAGE_TABLE: 'table',
  PUBLIC_ACQUISITIONS: 'public',
  ACQUISITION_MARKET: 'market',
  ACQUISITION_CATEGORY: 'category',
  ACQUISITION_LIST: 'list',
  GROWTH_EQUITY: 'growth-equity',
  VENTURE_PRIVATE: 'venture-private',
  AI_PULSE: 'ai-pulse',
  AI_HIRING: 'ai-hiring',
  AI_DEVELOPER: 'ai-developer',
  AI_CAPITAL: 'ai-capital',
  // Legacy route ids retained for normalization only.
  AI_MOMENTUM: 'ai-momentum',
  AI_DAILY: 'ai-daily',
};

export const MARKET_COVERAGE_PATH_BASE = '/marketinsights/coverage';

const TABLE_SKELETON = {
  kind: 'table',
  preset: 'compsheet',
  rows: 30,
  columns: 10,
  tableWidth: '98%',
  height: '75vh',
};

const ACQUISITION_DASHBOARD_SKELETON = {
  kind: 'chart',
  preset: 'barTrend',
  variant: 'bar',
  height: 620,
  width: '98%',
  minHeight: 520,
};

const ACQUISITION_CATEGORY_SKELETON = {
  kind: 'splitChartTable',
  chart: {
    kind: 'chart',
    preset: 'barTrend',
    height: 320,
    width: '98%',
    maxWidth: 1160,
  },
  table: {
    kind: 'table',
    preset: 'compsheet',
    rows: 10,
    columns: 6,
    height: 240,
    tableWidth: 1160,
    maxWidth: '98%',
  },
  minHeight: 600,
};

const COVERAGE_TABLE_VIEW = {
  id: MARKET_COVERAGE_VIEWS.COVERAGE_TABLE,
  label: 'Coverage Table',
  description: 'SoftwareIQ company coverage universe',
  icon: PublicIcon,
  requiredData: 'coverage',
  controls: ['category', 'ticker', 'columns'],
  layout: 'table',
  panels: [{ id: 'coverageTable', kind: 'table', skeleton: TABLE_SKELETON }],
  visualizations: ['table'],
  tableColumns: ['company', 'ticker', 'category', 'metricValue'],
};

const PRIVATE_FUNDING_TABLE_VIEW = {
  id: MARKET_COVERAGE_VIEWS.VENTURE_PRIVATE,
  label: 'Venture',
  description: 'Venture-backed software funding and valuation data',
  icon: RocketLaunchIcon,
  requiredData: 'vcFunding',
  controls: ['category', 'ticker', 'columns'],
  layout: 'table',
  panels: [{ id: 'privateFunding', kind: 'table', skeleton: TABLE_SKELETON }],
  visualizations: ['table'],
  tableColumns: ['company', 'ticker', 'category', 'date', 'metricValue'],
};

const SHOW_FULL_ACQUISITIONS = getDevFlag('ACQUISITIONS_FULL');
export const SHOW_AI_TRACKING = getDevFlag('AI_TRACKING');

const AI_TRACKING_SKELETONS = {
  chart: {
    kind: 'chart',
    preset: 'barTrend',
    height: 360,
    width: '100%',
    minHeight: 300,
  },
  table: {
    kind: 'table',
    preset: 'signal',
    rows: 14,
    columns: 12,
    height: 360,
    tableWidth: '100%',
  },
};

const buildAiTrackingView = ({
  id,
  label,
  description,
  icon,
  requiredData,
  controls,
  visualizations,
  tableColumns,
  layout = 'chartTable',
  panels,
} = {}) => ({
  id,
  label,
  description,
  icon,
  badge: 'Core',
  requiredData,
  controls,
  layout,
  viewLayout: 'splitPanel',
  ratio: 'primaryChart',
  panels: panels || [
    { id: 'chart', kind: 'chart', skeleton: AI_TRACKING_SKELETONS.chart },
    { id: 'table', kind: 'table', skeleton: AI_TRACKING_SKELETONS.table },
  ],
  visualizations,
  tableColumns,
});

const AI_TRACKING_VIEWS = [
  buildAiTrackingView({
    id: MARKET_COVERAGE_VIEWS.AI_PULSE,
    label: 'AI Pulse',
    description: 'What changed across AI-native hiring, GTM, developer adoption, ARR, and funding',
    icon: AutoAwesomeIcon,
    requiredData: 'aiTrackingPulse',
    controls: ['category', 'ticker', 'period', 'weeks', 'metric'],
    layout: 'table',
    panels: [{ id: 'pulseTable', kind: 'table', skeleton: AI_TRACKING_SKELETONS.table }],
    visualizations: ['kpiStrip', 'table'],
    tableColumns: ['company', 'category', 'metricValue', 'trendDelta', 'percent', 'date'],
  }),
  buildAiTrackingView({
    id: MARKET_COVERAGE_VIEWS.AI_HIRING,
    label: 'Hiring & GTM',
    description: 'Hiring velocity, sales-force scaling, and GTM versus product investment',
    icon: GroupsIcon,
    requiredData: 'aiTrackingHiring',
    controls: ['category', 'ticker', 'period', 'weeks', 'metric'],
    visualizations: ['trendLine', 'roleMix', 'table'],
    tableColumns: ['company', 'metricValue', 'trendDelta', 'percent', 'date'],
  }),
  buildAiTrackingView({
    id: MARKET_COVERAGE_VIEWS.AI_DEVELOPER,
    label: 'Developer Adoption',
    description: 'Product-level developer usage, growth, and comparable-product share',
    icon: CodeIcon,
    requiredData: 'aiTrackingDeveloper',
    controls: ['period', 'weeks', 'aggregate', 'category', 'metric', 'view'],
    visualizations: ['trendLine', 'stackedBar', 'table'],
    tableColumns: ['company', 'ticker', 'category', 'metricValue', 'trendDelta', 'percent', 'date'],
  }),
  buildAiTrackingView({
    id: MARKET_COVERAGE_VIEWS.AI_CAPITAL,
    label: 'Capital & ARR',
    description: 'Sourced ARR trajectories, funding rounds, and valuation changes',
    icon: AccountBalanceIcon,
    requiredData: 'aiTrackingCapital',
    controls: ['category', 'ticker'],
    visualizations: ['trendLine', 'groupedBar', 'table'],
    tableColumns: ['company', 'metricValue', 'date', 'link'],
  }),
];

const ACQUISITION_VIEWS = [
  ...(SHOW_FULL_ACQUISITIONS ? [
    {
      id: MARKET_COVERAGE_VIEWS.ACQUISITION_MARKET,
      label: 'Market',
      description: 'Total software acquisition activity across categories',
      icon: DashboardIcon,
      requiredData: 'acquisitionAnalytics',
      controls: ['category', 'ticker'],
      layout: 'dashboardPanel',
      panels: [{ id: 'acquisitionMarket', kind: 'chart', skeleton: ACQUISITION_DASHBOARD_SKELETON }],
      visualizations: ['groupedBar', 'table'],
      tableColumns: ['company', 'ticker', 'date', 'metricValue'],
    },
    {
      id: MARKET_COVERAGE_VIEWS.ACQUISITION_CATEGORY,
      label: 'Category',
      description: 'Category acquisition trends and actor mix',
      icon: CategoryIcon,
      requiredData: 'acquisitionAnalytics',
      controls: ['category', 'ticker'],
      layout: 'dashboardPanel',
      panels: [{ id: 'acquisitionCategory', kind: 'chart', skeleton: ACQUISITION_CATEGORY_SKELETON }],
      visualizations: ['groupedBar', 'table'],
      tableColumns: ['category', 'date', 'metricValue'],
    },
    {
      id: MARKET_COVERAGE_VIEWS.ACQUISITION_LIST,
      label: 'List',
      description: 'Total and public company acquisition transaction lists',
      icon: GridOnIcon,
      requiredData: 'acquisitionAnalytics',
      controls: ['category', 'ticker', 'sponsor', 'columns'],
      layout: 'table',
      panels: [{ id: 'acquisitionList', kind: 'table', skeleton: TABLE_SKELETON }],
      visualizations: ['table'],
      tableColumns: ['company', 'ticker', 'date', 'metricValue'],
    },
  ] : []),
  {
    id: MARKET_COVERAGE_VIEWS.PUBLIC_ACQUISITIONS,
    label: 'Public Acquisitions',
    description: 'Public software takeout activity',
    icon: BusinessIcon,
    requiredData: 'publicAcquisitions',
    controls: ['category', 'ticker', 'columns'],
    layout: 'table',
    panels: [{ id: 'publicAcquisitions', kind: 'table', skeleton: TABLE_SKELETON }],
    visualizations: ['table'],
    tableColumns: ['company', 'ticker', 'date', 'metricValue', 'percent'],
  },
];

export const MARKET_COVERAGE_WORKSPACE_DESCRIPTOR = {
  id: 'marketCoverage',
  title: 'Market Coverage',
  description: 'Coverage, acquisition, and private funding datasets for software markets.',
  icon: PublicIcon,
  defaultSectionId: MARKET_COVERAGE_SECTIONS.COVERAGE,
  defaultViewId: MARKET_COVERAGE_VIEWS.COVERAGE_TABLE,
  sections: [
    {
      id: MARKET_COVERAGE_SECTIONS.COVERAGE,
      label: 'Coverage',
      description: 'SoftwareIQ company coverage universe',
      icon: PublicIcon,
      views: [COVERAGE_TABLE_VIEW],
    },
    {
      id: MARKET_COVERAGE_SECTIONS.ACQUISITIONS,
      label: 'Acquisitions',
      description: 'Public acquisition comps and total M&A analytics',
      icon: BusinessIcon,
      views: ACQUISITION_VIEWS,
    },
    {
      id: MARKET_COVERAGE_SECTIONS.PRIVATE_FUNDING,
      label: 'Venture',
      description: 'Venture-backed software funding and valuation coverage',
      icon: RocketLaunchIcon,
      views: [
        PRIVATE_FUNDING_TABLE_VIEW,
        ...(SHOW_AI_TRACKING ? AI_TRACKING_VIEWS : []),
      ],
    },
  ],
};

export const normalizeMarketCoverageView = (sectionId, viewId) => {
  const normalizedAiViewId = (
    sectionId === MARKET_COVERAGE_SECTIONS.PRIVATE_FUNDING
    && [MARKET_COVERAGE_VIEWS.AI_MOMENTUM, MARKET_COVERAGE_VIEWS.AI_DAILY].includes(viewId)
  )
    ? MARKET_COVERAGE_VIEWS.AI_PULSE
    : viewId;
  const normalizedViewId = (
    sectionId === MARKET_COVERAGE_SECTIONS.PRIVATE_FUNDING
    && normalizedAiViewId === MARKET_COVERAGE_VIEWS.GROWTH_EQUITY
  )
    ? MARKET_COVERAGE_VIEWS.VENTURE_PRIVATE
    : (
      sectionId === MARKET_COVERAGE_SECTIONS.ACQUISITIONS
      && ['analytics', 'total', 'acquirers', 'sponsors'].includes(viewId)
    )
      ? MARKET_COVERAGE_VIEWS.ACQUISITION_LIST
    : normalizedAiViewId;
  const route = normalizeWorkspaceDescriptorRoute({
    descriptor: MARKET_COVERAGE_WORKSPACE_DESCRIPTOR,
    sectionId,
    viewId: normalizedViewId,
  });
  return route;
};

export const buildMarketCoveragePath = ({
  sectionId = MARKET_COVERAGE_SECTIONS.COVERAGE,
  viewId,
} = {}) => {
  const route = normalizeMarketCoverageView(sectionId, viewId);
  return `${MARKET_COVERAGE_PATH_BASE}/${route.sectionId}/${route.viewId}`;
};

export const MARKET_COVERAGE_WORKSPACE_CONFIG = {
  ...normalizeWorkspaceDescriptor(MARKET_COVERAGE_WORKSPACE_DESCRIPTOR),
  buildPath: ({ sectionId, viewId }) => buildMarketCoveragePath({ sectionId, viewId }),
  resolveRoute: ({ sectionId, viewId }) => normalizeMarketCoverageView(sectionId, viewId),
  shouldRedirect: ({ sectionId, viewId, routeState = {}, resolvedRoute }) => (
    routeState.legacy === true || sectionId !== resolvedRoute.sectionId || viewId !== resolvedRoute.viewId
  ),
  getChips: ({ resolvedRoute }) => {
    const chips = buildWorkspaceDescriptorChips({
      descriptor: MARKET_COVERAGE_WORKSPACE_DESCRIPTOR,
      sectionId: resolvedRoute.sectionId,
      viewId: resolvedRoute.viewId,
    });
    const seen = new Set();
    return chips.filter((chip) => {
      const label = String(chip?.label || '').trim();
      if (!label || seen.has(label)) return false;
      seen.add(label);
      return true;
    });
  },
};

export const parseMarketCoveragePath = (pathname = '') => {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  if (normalizedPath === '/marketinsights/acquisitions') {
    return {
      sectionId: MARKET_COVERAGE_SECTIONS.ACQUISITIONS,
      viewId: MARKET_COVERAGE_VIEWS.PUBLIC_ACQUISITIONS,
      legacy: true,
    };
  }
  if (normalizedPath === '/marketinsights/venture-backed') {
    return {
      sectionId: MARKET_COVERAGE_SECTIONS.PRIVATE_FUNDING,
      viewId: MARKET_COVERAGE_VIEWS.VENTURE_PRIVATE,
      legacy: true,
    };
  }

  const parts = normalizedPath.split('/').filter(Boolean);
  if (parts[0] !== 'marketinsights' || parts[1] !== 'coverage') return null;
  const sectionId = parts[2];
  const viewId = parts[3];

  const legacyCadence = viewId === MARKET_COVERAGE_VIEWS.AI_DAILY ? 'daily' : null;
  return {
    sectionId,
    viewId: sectionId === MARKET_COVERAGE_SECTIONS.PRIVATE_FUNDING
      && [MARKET_COVERAGE_VIEWS.GROWTH_EQUITY, MARKET_COVERAGE_VIEWS.AI_MOMENTUM, MARKET_COVERAGE_VIEWS.AI_DAILY].includes(viewId)
      ? viewId === MARKET_COVERAGE_VIEWS.GROWTH_EQUITY
        ? MARKET_COVERAGE_VIEWS.VENTURE_PRIVATE
        : MARKET_COVERAGE_VIEWS.AI_PULSE
      : viewId,
    legacy: parts.length < 4 || (
      sectionId === MARKET_COVERAGE_SECTIONS.PRIVATE_FUNDING
      && [
        MARKET_COVERAGE_VIEWS.GROWTH_EQUITY,
        MARKET_COVERAGE_VIEWS.AI_MOMENTUM,
        MARKET_COVERAGE_VIEWS.AI_DAILY,
      ].includes(viewId)
    ),
    ...(legacyCadence ? { legacyCadence } : {}),
  };
};

export const isMarketCoveragePath = (pathname = '') => Boolean(parseMarketCoveragePath(pathname));

export const buildLegacyMarketCoverageRedirectPath = (pathname = '') => {
  const route = parseMarketCoveragePath(pathname);
  if (!route) return buildMarketCoveragePath();
  const path = buildMarketCoveragePath(route);
  return route.legacyCadence ? `${path}?cadence=${route.legacyCadence}` : path;
};

export const buildMarketCoverageSidebarItems = () => (
  buildWorkspaceDescriptorSidebarItems({
    descriptor: MARKET_COVERAGE_WORKSPACE_DESCRIPTOR,
    buildPath: ({ sectionId, viewId }) => buildMarketCoveragePath({ sectionId, viewId }),
  })
);
